import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { assetUrl } from '../services/api';
import { SectionHero } from './updatehero';

export interface HeroSectionData {
  id: string;
  heading: string;
  description: string;
  type: 'tesseract' | 'nova' | 'grid';
  gradient: string;
  use_type?: boolean;
  use_video?: boolean;
  video_url?: string | null;
  video_type?: 'background' | 'foreground' | string;
  video_settings?: unknown;
  heading_color?: string;
  description_color?: string;
  description_bg_color?: string;
  button_text?: string;
  button_action?: 'modal' | 'link';
  button_link?: string | null;
  button_bg?: string | null;
  button_text_color?: string | null;
  button_hover_gradient_start?: string | null;
  button_hover_gradient_end?: string | null;
  heading_overlay_color?: string;
  heading_shadow_color?: string;
  heading_overlay_enabled?: boolean;
  heading_shadow_enabled?: boolean;
}

interface HeroProps {
  onContactClick: () => void;
  data?: HeroSectionData[];
  videoBlobUrl?: string | null;
}

// --- 3D COMPONENTS ---

// 1. Holographic Tesseract (Exhibitions)
const Tesseract = () => {
  const size = 300;
  const offset = size / 2;
  const outerFaceStyle = "absolute inset-0 border-[2px] border-amber-400 bg-amber-950/10 shadow-[0_0_30px_rgba(255,191,0,0.2)_inset]";
  const innerFaceStyle = "absolute inset-0 border border-amber-500 bg-amber-900/20";

  return (
    <motion.div
      initial={{ scale: 0.5, rotateY: 90, opacity: 0 }}
      animate={{ scale: 1, rotateY: 0, opacity: 1 }}
      exit={{ scale: 1.2, rotateY: -90, opacity: 0 }}
      transition={{ duration: 0.8, ease: "backOut" }}
      className="relative w-[280px] h-[280px] md:w-[400px] md:h-[400px] perspective-1000 transform-style-3d will-change-transform"
    >
      <motion.div
        className="absolute inset-0 w-full h-full transform-style-3d will-change-transform"
        animate={{ rotateX: [0, 360], rotateY: [0, 360] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
      >
        {[0, 90, 180, 270].map((deg) => (
          <div key={deg} className={outerFaceStyle} style={{ transform: `rotateY(${deg}deg) translateZ(${offset}px)` }}>
            <div className="absolute inset-2 border border-amber-400/30"></div>
          </div>
        ))}
        <div className={outerFaceStyle} style={{ transform: `rotateX(90deg) translateZ(${offset}px)` }} />
        <div className={outerFaceStyle} style={{ transform: `rotateX(-90deg) translateZ(${offset}px)` }} />
      </motion.div>

      <motion.div
        className="absolute top-1/2 left-1/2 w-[180px] h-[180px] -ml-[90px] -mt-[90px] transform-style-3d will-change-transform"
        animate={{ rotateX: [360, 0], rotateY: [360, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
      >
        {[0, 90, 180, 270].map((deg) => (
          <div key={deg} className={innerFaceStyle} style={{ transform: `rotateY(${deg}deg) translateZ(90px)` }} />
        ))}
        <div className={innerFaceStyle} style={{ transform: `rotateX(90deg) translateZ(90px)` }} />
        <div className={innerFaceStyle} style={{ transform: `rotateX(-90deg) translateZ(90px)` }} />
        <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-white rounded-full blur-[20px] animate-pulse-glow -translate-x-1/2 -translate-y-1/2 shadow-[0_0_50px_rgba(255,255,255,0.8)]"></div>
      </motion.div>
    </motion.div>
  );
};

// 2. Pulse Nova (Events)
const PulseNova = () => {
  return (
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 1.5, opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="relative w-[300px] h-[300px] md:w-[450px] md:h-[450px] flex items-center justify-center transform-style-3d will-change-transform"
    >
      <motion.div
        animate={{ scale: [1, 1.4, 1], filter: ["hue-rotate(0deg)", "hue-rotate(90deg)", "hue-rotate(0deg)"] }}
        transition={{ duration: 3, repeat: Infinity }}
        className="w-40 h-40 bg-gradient-to-tr from-amber-600 to-amber-400 rounded-full blur-[40px] absolute z-0 will-change-transform"
      />
      <div className="w-24 h-24 bg-white rounded-full blur-[20px] absolute z-10" />

      {[1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          className={`absolute border-[2px] ${i % 2 === 0 ? 'border-amber-500/60' : 'border-amber-400/60'} rounded-full will-change-transform`}
          style={{ 
            width: 200 + i * 40, 
            height: 200 + i * 40, 
            transformStyle: 'preserve-3d',
            borderStyle: i === 3 ? 'dashed' : 'solid'
          }}
          animate={{ rotateX: [0, 360], rotateY: [0, 360], rotateZ: [0, i % 2 === 0 ? 360 : -360] }}
          transition={{ duration: 8 + i * 2, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute top-0 left-1/2 w-3 h-3 bg-white rounded-full blur-[2px] shadow-[0_0_15px_white]" />
        </motion.div>
      ))}
    </motion.div>
  );
};

// 3. Architectural Grid (Fit-out)
const ArchGrid = () => {
  const planeStyle = "absolute border border-amber-400/50 bg-amber-900/10 shadow-[0_0_15px_rgba(255,191,0,0.1)] backdrop-blur-[0px]";
  return (
    <motion.div
      initial={{ rotateX: 60, scale: 0.8, opacity: 0 }}
      animate={{ rotateX: 60, scale: 1, opacity: 1 }}
      exit={{ rotateX: 0, scale: 0.8, opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="relative w-[300px] h-[300px] md:w-[500px] md:h-[500px] transform-style-3d perspective-1000 will-change-transform"
    >
      <motion.div
        className="absolute inset-0 transform-style-3d will-change-transform"
        animate={{ rotateZ: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      >
        {/* Base Grid */}
        <div className="absolute inset-[-50%] w-[200%] h-[200%] bg-[linear-gradient(rgba(255,191,0,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(255,191,0,0.2)_1px,transparent_1px)] bg-[size:40px_40px] border border-amber-500/30 rounded-full opacity-50"></div>

        {/* Floating Planes */}
        <motion.div 
          className={`${planeStyle} w-40 h-60 top-1/4 left-1/4`} 
          animate={{ translateZ: [0, 50, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className={`${planeStyle} w-60 h-40 bottom-1/4 right-1/4`} 
          animate={{ translateZ: [20, 80, 20] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />
        
        {/* Scanning Laser */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-t from-amber-500/0 via-amber-400/20 to-amber-500/0 h-[20%]"
          animate={{ top: ["-20%", "120%"] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
      </motion.div>
    </motion.div>
  );
};

// Map types to components
const Visualizers = {
  tesseract: Tesseract,
  nova: PulseNova,
  grid: ArchGrid
};

export const Hero: React.FC<HeroProps> = ({ onContactClick }) => {
  return <SectionHero onContactClick={onContactClick} />;
};
